<template>
  <div v-if="visible" class="wl-dialog-mask" @click.self="close">
    <TanchuangFrame title="乌拉斯台口岸" v-slot="{ backgroundStyle }">
      <section class="wl-dialog" :style="backgroundStyle">
        <div class="wl-dialog__tools">
          <div class="wl-tool-actions">
            <button type="button" class="wl-tool-btn" @click="openStatisticsDialog">导出</button>
            <button type="button" class="wl-tool-btn" @click="close">关闭</button>
          </div>
          <div class="wl-period-switch">
            <button
              v-for="(item, index) in periodTabs"
              :key="item"
              type="button"
              :class="{ active: index === localPeriodIndex }"
              @click="switchPeriod(index)"
            >
              {{ item }}
            </button>
          </div>
        </div>

        <div class="wl-dialog__grid">
          <article class="wl-chart-card wl-chart-card--primary">
            <TabHeader
              class="wl-chart-header wl-chart-header--primary"
              :items="primaryTabs"
              :activeIndex="primaryTabIndex"
              title-mode="switch"
              @change="switchPrimaryTab"
            ></TabHeader>
            <div class="wl-unit">{{ primaryChartUnit }}</div>
            <div ref="primaryChart" class="wl-chart-box"></div>
          </article>

          <article class="wl-chart-card wl-chart-card--inspection">
            <TabHeader :items="['查检作业情况']" :activeIndex="0"></TabHeader>
            <div class="inspection-chart-shell">
              <div class="inspection-chart-head">
                <span class="inspection-axis-unit">{{ inspectionAxisUnit }}</span>
                <div class="inspection-summary">
                  <div
                    v-for="item in inspectionMetrics"
                    :key="item.label"
                    class="inspection-metric"
                  >
                    <span class="inspection-legend-item inspection-metric-label">
                      <i class="inspection-legend-swatch" :style="{ background: item.color }"></i>
                      <span>{{ item.label }}</span>
                    </span>
                    <span class="inspection-value" :class="item.valueClass">{{ item.value }}</span>
                  </div>
                </div>
              </div>
              <div ref="inspectionChart" class="wl-chart-box inspection-chart-box"></div>
            </div>
          </article>

          <article class="wl-chart-card">
            <div class="wl-chart-header">
              <div class="wl-card-title">监管运输工具数量情况</div>
            </div>
            <div class="wl-unit">辆</div>
            <div ref="transportChart" class="wl-chart-box"></div>
          </article>

          <article class="wl-chart-card wl-chart-card--passenger">
            <div class="wl-chart-header">
              <div class="wl-card-title">进出境旅客情况</div>
            </div>
            <div class="wl-passenger-stats">
              <div v-for="item in passengerStats" :key="item.label">
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
              </div>
            </div>
            <div ref="passengerChart" class="wl-chart-box wl-chart-box--passenger"></div>
          </article>
        </div>
      </section>
    </TanchuangFrame>

    <StatisticsDialog
      :visible="showStatisticsDialog"
      source="wl"
      :metric-options="statisticsMetricOptions"
      :default-metric="currentStatisticsMetric"
      @close="closeStatisticsDialog"
    />
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getWlMiddleChart, getWlOverview, getWlPassengerChart, getWlTerritoryChart } from '@/api/datav'
import StatisticsDialog from '@/components/common/StatisticsDialog.vue'
import TabHeader from '@/components/common/TabHeader.vue'
import TanchuangFrame from '@/components/common/TanchuangFrame.vue'

export default {
  name: 'WlRegionDialog',
  components: { TabHeader, StatisticsDialog, TanchuangFrame },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    periodKey: {
      type: String,
      default: 'day'
    },
    dateRange: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      overviewData: {},
      remoteInspectionData: {
        middle: null,
        territory: null
      },
      remotePassengerData: null,
      chartInstances: [],
      localPeriodKey: ['day', 'month', 'year'].includes(this.periodKey) ? this.periodKey : 'day',
      primaryTabIndex: 0,
      primaryTabs: ['报关单数量', '货值', '货运量'],
      periodTabs: ['日数据', '月数据', '年数据'],
      inspectionBtns: ['事中查验'],
      inspectionBtnIdx: 0,
      inspectionHoverIndex: null,
      inspectionLegend: [
        { key: 'pending', color: '#19dcff' },
        { key: 'today', color: '#ffd247' },
        { key: 'rate', color: '#82f34d' }
      ],
      statisticsMetricOptions: ['事中查验', '进出境旅客情况', '监管运输工具数量', '报关单数量', '货值', '货运量'],
      showStatisticsDialog: false
    }
  },
  computed: {
    localPeriodIndex() {
      const index = ['day', 'month', 'year'].indexOf(this.localPeriodKey)
      return index >= 0 ? index : 0
    },
    primaryChartData() {
      const map = [this.overviewData.form, this.overviewData.goods, this.overviewData.freight]
      return map[this.primaryTabIndex] || map[0]
    },
    primaryChartUnit() {
      return ['票', '亿', '吨'][this.primaryTabIndex] || '票'
    },
    currentStatisticsMetric() {
      return '事中查验'
    },
    currentInspectionTypeKey() {
      return 'middle'
    },
    resolvedInspectionData() {
      const remoteInspection = this.remoteInspectionData[this.currentInspectionTypeKey]
      return remoteInspection || this.getFallbackInspectionData()
    },
    passengerStats() {
      const summary = this.remotePassengerData && this.remotePassengerData.summary
        ? this.remotePassengerData.summary
        : {}
      return [
        { label: '检疫传染病', value: `${this.formatPassengerCountValue(summary.quarantineNum)}例` },
        { label: '监测传染病', value: `${this.formatPassengerCountValue(summary.detectionNum)}次` },
        { label: '一般疾病', value: `${this.formatPassengerCountValue(summary.averageNum)}次` }
      ]
    },
    inspectionMetrics() {
      return [
        {
          ...this.inspectionLegend[0],
          label: '待查验量',
          value: this.resolvedInspectionSummary.pending,
          valueClass: 'inspection-value--pending'
        },
        {
          ...this.inspectionLegend[1],
          label: this.inspectionProcessingLabel,
          value: this.resolvedInspectionSummary.today,
          valueClass: 'inspection-value--today'
        },
        {
          ...this.inspectionLegend[2],
          label: this.inspectionExceptionLabel,
          value: this.resolvedInspectionSummary.rate,
          valueClass: 'inspection-value--rate'
        }
      ]
    },
    inspectionProcessingLabel() {
      return '已查验量'
    },
    inspectionExceptionLabel() {
      return '查获率'
    },
    inspectionAxisUnit() {
      return '个/%'
    },
    resolvedInspectionSummary() {
      const summary = this.resolvedInspectionData.summary || { pending: 0, today: 0, rate: 0 }
      const hoverIndex = this.inspectionHoverIndex
      const pendingSeries = Array.isArray(this.resolvedInspectionData.pending) ? this.resolvedInspectionData.pending : []
      const todaySeries = Array.isArray(this.resolvedInspectionData.today) ? this.resolvedInspectionData.today : []
      const rateSeries = Array.isArray(this.resolvedInspectionData.rate) ? this.resolvedInspectionData.rate : []

      if (
        Number.isInteger(hoverIndex) &&
        hoverIndex >= 0 &&
        hoverIndex < pendingSeries.length &&
        hoverIndex < todaySeries.length &&
        hoverIndex < rateSeries.length
      ) {
        return {
          pending: this.formatInspectionCountValue(pendingSeries[hoverIndex]),
          today: this.formatInspectionCountValue(todaySeries[hoverIndex]),
          rate: this.formatInspectionThirdValue(rateSeries[hoverIndex])
        }
      }

      return {
        pending: this.formatInspectionCountValue(summary.pending),
        today: this.formatInspectionCountValue(summary.today),
        rate: this.formatInspectionThirdValue(summary.rate)
      }
    }
  },
  watch: {
    visible(value) {
      if (value) {
        this.localPeriodKey = this.normalizePeriod(this.periodKey)
        this.open()
      } else {
        this.showStatisticsDialog = false
        this.clearInspectionHoverIndex()
        this.disposeCharts()
      }
    },
    periodKey(value) {
      if (!this.visible) {
        this.localPeriodKey = this.normalizePeriod(value)
      }
    },
    dateRange: {
      deep: true,
      handler() {
        if (this.visible) {
          this.open()
        }
      }
    }
  },
  mounted() {
    window.addEventListener('resize', this.resize)
    if (this.visible) {
      this.open()
    }
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.resize)
    this.disposeCharts()
  },
  methods: {
    close() {
      this.showStatisticsDialog = false
      this.$emit('close')
    },
    async open() {
      await this.fetchOverview()
      this.$nextTick(() => {
        if (!this.chartInstances.length) {
          this.initCharts()
          return
        }
        this.renderAllCharts()
      })
    },
    async fetchOverview() {
      const dateRange = this.dateRange || {}
      try {
        const [overview, middleData, territoryData, passengerData] = await Promise.all([
          getWlOverview(this.localPeriodKey, dateRange),
          getWlMiddleChart(this.localPeriodKey, dateRange),
          getWlTerritoryChart(this.localPeriodKey, dateRange),
          getWlPassengerChart(this.localPeriodKey, dateRange)
        ])
        this.overviewData = overview || {}
        this.remoteInspectionData = {
          middle: middleData || null,
          territory: territoryData || null
        }
        this.remotePassengerData = passengerData || null
      } catch (error) {
        console.error('乌拉斯台弹窗数据加载失败', error)
      }
    },
    normalizePeriod(period) {
      return ['day', 'month', 'year'].includes(period) ? period : 'day'
    },
    switchPeriod(index) {
      const nextPeriod = ['day', 'month', 'year'][index] || 'day'
      if (nextPeriod === this.localPeriodKey) {
        return
      }
      this.localPeriodKey = nextPeriod
      this.clearInspectionHoverIndex()
      this.open()
    },
    switchPrimaryTab(index) {
      this.primaryTabIndex = index
      this.$nextTick(() => {
        this.renderPrimaryChart()
      })
    },
    switchInspectionTab(index) {
      if (this.inspectionBtnIdx === index) {
        return
      }
      this.inspectionBtnIdx = index
      this.clearInspectionHoverIndex()
      this.$nextTick(() => {
        this.updateInspectionChart()
      })
    },
    openStatisticsDialog() {
      this.showStatisticsDialog = true
    },
    closeStatisticsDialog() {
      this.showStatisticsDialog = false
    },
    initCharts() {
      if (!this.$refs.primaryChart || !this.$refs.transportChart || !this.$refs.inspectionChart || !this.$refs.passengerChart) {
        return
      }

      this.disposeCharts()
      this.primaryChart = echarts.init(this.$refs.primaryChart)
      this.transportChart = echarts.init(this.$refs.transportChart)
      this.inspectionChart = echarts.init(this.$refs.inspectionChart)
      this.passengerChart = echarts.init(this.$refs.passengerChart)
      this.chartInstances = [this.primaryChart, this.transportChart, this.inspectionChart, this.passengerChart]

      if (!this.inspectionGlobalOutHandler) {
        this.inspectionGlobalOutHandler = () => {
          this.clearInspectionHoverIndex()
        }
      }
      const zr = this.inspectionChart.getZr()
      if (zr && typeof zr.on === 'function') {
        zr.on('globalout', this.inspectionGlobalOutHandler)
      }

      this.renderAllCharts()
    },
    renderAllCharts() {
      this.renderPrimaryChart()

      if (this.transportChart) {
        this.transportChart.setOption(this.buildBarOption(this.getTransportChartDataWithoutRailway(), {
          unit: '辆',
          showAxisName: false,
          colors: ['#5bdcff', '#1a88e8', '#ffb340', '#f46f1f'],
          top: 72,
          barWidth: 10
        }), true)
      }

      this.updateInspectionChart()

      if (this.passengerChart) {
        this.passengerChart.setOption(this.buildPassengerOption(), true)
      }
    },
    renderPrimaryChart() {
      if (!this.primaryChart) {
        return
      }
      this.primaryChart.setOption(this.buildBarOption(this.primaryChartData, {
        unit: this.primaryChartUnit,
        showAxisName: false,
        colors: ['#5bdcff', '#1a88e8', '#ffdb6a', '#ff7b21'],
        top: 72,
        barWidth: 13
      }), true)
    },
    updateInspectionChart() {
      if (!this.inspectionChart) {
        return
      }

      const currentData = this.resolvedInspectionData
      const processingLabel = this.inspectionProcessingLabel
      const countValues = []
        .concat(currentData.pending || [])
        .concat(currentData.today || [])
        .map(item => Number(item) || 0)
      const countMax = countValues.length ? Math.max(...countValues, 0) : 0
      const countAxisMax = Math.max(30, Math.ceil(countMax / 5) * 5)
      const countAxisInterval = Math.max(Math.ceil(countAxisMax / 6), 1)
      const thirdAxisMax = this.currentInspectionTypeKey === 'territory' ? countAxisMax : 100
      const thirdAxisInterval = this.currentInspectionTypeKey === 'territory' ? countAxisInterval : 20

      this.inspectionChart.setOption({
        color: ['#19dcff', '#ffd247', '#82f34d'],
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(7, 28, 68, 0.96)',
          borderColor: 'rgba(87, 196, 255, 0.28)',
          textStyle: { color: '#e6f8ff', fontSize: 11 },
          formatter: params => this.formatInspectionTooltip(params, processingLabel),
          axisPointer: {
            lineStyle: { color: 'rgba(116, 210, 255, 0.28)' }
          }
        },
        legend: { show: false },
        grid: { left: 12, right: 8, top: 14, bottom: 6, containLabel: true },
        xAxis: {
          type: 'category',
          boundaryGap: true,
          data: currentData.labels || [],
          axisLine: { lineStyle: { color: 'rgba(111, 180, 232, 0.34)', width: 1 } },
          axisLabel: { color: '#bfdcf3', fontSize: 10, margin: 13 },
          axisTick: { show: false },
          splitLine: { show: false }
        },
        yAxis: [
          {
            type: 'value',
            min: 0,
            max: countAxisMax,
            interval: countAxisInterval,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { lineStyle: { color: 'rgba(138, 211, 255, 0.16)', width: 1 } },
            axisLabel: { color: '#a8d2ef', fontSize: 10, margin: 10 }
          },
          {
            type: 'value',
            min: 0,
            max: thirdAxisMax,
            interval: thirdAxisInterval,
            axisLine: { show: false },
            axisTick: { show: false },
            splitLine: { show: false },
            axisLabel: {
              color: '#b8f58c',
              fontSize: 10,
              margin: 10,
              formatter: value => this.currentInspectionTypeKey === 'territory' ? value : `${value}%`
            }
          }
        ],
        series: [
          {
            name: '待查验量',
            type: 'bar',
            barWidth: 13,
            barGap: '35%',
            itemStyle: {
              borderRadius: [3, 3, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#5be5ff' },
                { offset: 1, color: '#1584da' }
              ])
            },
            data: currentData.pending || []
          },
          {
            name: processingLabel,
            type: 'bar',
            barWidth: 13,
            itemStyle: {
              borderRadius: [3, 3, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#ffe37a' },
                { offset: 1, color: '#d79318' }
              ])
            },
            data: currentData.today || []
          },
          {
            name: this.inspectionExceptionLabel,
            type: 'line',
            yAxisIndex: 1,
            smooth: 0.42,
            showSymbol: true,
            symbol: 'circle',
            symbolSize: 7,
            lineStyle: {
              width: 3,
              color: '#82f34d',
              shadowBlur: 8,
              shadowColor: 'rgba(130, 243, 77, 0.16)'
            },
            itemStyle: {
              color: '#82f34d'
            },
            data: currentData.rate || []
          }
        ]
      }, { notMerge: true })
    },
    formatInspectionTooltip(params, processingLabel) {
      const rows = Array.isArray(params) ? params : []
      if (!rows.length) {
        this.clearInspectionHoverIndex()
        return ''
      }

      const dataIndex = rows[0] && Number.isInteger(rows[0].dataIndex) ? rows[0].dataIndex : null
      this.inspectionHoverIndex = dataIndex
      const title = rows[0] && rows[0].axisValueLabel ? rows[0].axisValueLabel : ''

      const labelMap = {
        待查验量: '待查验量',
        [processingLabel]: processingLabel,
        [this.inspectionExceptionLabel]: this.inspectionExceptionLabel
      }

      const lines = rows.map(item => {
        const label = labelMap[item.seriesName] || item.seriesName
        const markerColor = item.seriesName === '待查验量'
          ? '#19dcff'
          : item.seriesName === processingLabel
            ? '#ffd247'
            : '#82f34d'
        const marker = `<span style="display:inline-block;width:10px;height:10px;margin-right:8px;border-radius:50%;background:${markerColor};"></span>`
        const value = item.seriesName === this.inspectionExceptionLabel
          ? this.formatInspectionThirdValue(item.data)
          : this.formatInspectionCountValue(item.data)
        return `<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;margin-top:6px;"><span style="display:inline-flex;align-items:center;">${marker}${label}</span><strong style="color:#ffffff;font-weight:700;">${value}</strong></div>`
      })

      return `<div><div style="margin-bottom:2px;font-weight:700;color:#dff4ff;">${title}</div>${lines.join('')}</div>`
    },
    clearInspectionHoverIndex() {
      this.inspectionHoverIndex = null
    },
    buildBarOption(data, config) {
      const chartData = data || {}
      const series = Array.isArray(chartData.series) ? chartData.series : []
      const colors = config.colors
      return {
        tooltip: this.getTooltip(),
        legend: {
          data: chartData.legend || series.map(item => item.name),
          right: 20,
          top: 16,
          icon: 'rect',
          itemWidth: 13,
          itemHeight: 13,
          itemGap: 20,
          textStyle: { color: '#d6efff', fontSize: 13 }
        },
        grid: { left: 48, right: 18, top: config.top, bottom: 34 },
        xAxis: this.getCategoryAxis(chartData.labels || []),
        yAxis: this.getValueAxis(config.showAxisName === false ? '' : config.unit),
        series: series.map((item, index) => ({
          name: item.name,
          type: 'bar',
          barWidth: config.barWidth,
          itemStyle: {
            borderRadius: [3, 3, 0, 0],
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: colors[(index * 2) % colors.length] },
              { offset: 1, color: colors[(index * 2 + 1) % colors.length] }
            ])
          },
          data: item.data || []
        }))
      }
    },
    buildPassengerOption() {
      const passengerData = this.getPassengerChartData()
      return {
        tooltip: this.getTooltip(),
        legend: {
          data: ['进境', '出境'],
          top: 6,
          left: 'center',
          icon: 'rect',
          itemWidth: 13,
          itemHeight: 13,
          itemGap: 20,
          textStyle: { color: '#d6efff', fontSize: 13 }
        },
        grid: { left: 42, right: 18, top: 54, bottom: 34 },
        xAxis: this.getCategoryAxis(passengerData.labels),
        yAxis: this.getValueAxis('人次'),
        series: [
          {
            name: '进境',
            type: 'bar',
            barWidth: 13,
            itemStyle: {
              borderRadius: [3, 3, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#ffdb6a' },
                { offset: 1, color: '#ff7b21' }
              ])
            },
            data: passengerData.entry
          },
          {
            name: '出境',
            type: 'bar',
            barWidth: 13,
            itemStyle: {
              borderRadius: [3, 3, 0, 0],
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: '#5bdcff' },
                { offset: 1, color: '#1a88e8' }
              ])
            },
            data: passengerData.exit
          }
        ]
      }
    },
    getFallbackInspectionData() {
      return {
        labels: ['04/20', '07/18', '10/22', '01/15', '03/29', '04/01', '04/02'],
        pending: [92, 108, 102, 116, 97, 103, 106],
        today: [78, 94, 91, 101, 86, 89, 92],
        rate: [16, 18, 17, 19, 17, 18, 18],
        summary: {
          pending: 107,
          today: 92,
          rate: 19.7
        }
      }
    },
    getPassengerChartData() {
      const chartData = this.remotePassengerData || {}
      const series = Array.isArray(chartData.series) ? chartData.series : []
      const getSeriesData = name => {
        const item = series.find(seriesItem => seriesItem && seriesItem.name === name)
        return item && Array.isArray(item.data) ? item.data : []
      }
      return {
        labels: Array.isArray(chartData.labels) ? chartData.labels : [],
        entry: getSeriesData('进境'),
        exit: getSeriesData('出境')
      }
    },
    getTransportChartDataWithoutRailway() {
      const chartData = this.overviewData.transportation || {}
      const series = Array.isArray(chartData.series)
        ? chartData.series.filter(item => item && item.name !== '铁路')
        : []
      const legend = Array.isArray(chartData.legend)
        ? chartData.legend.filter(item => item !== '铁路')
        : ['公路进口', '公路出口']
      return {
        ...chartData,
        legend,
        series
      }
    },
    formatPassengerCountValue(value) {
      const num = Number(value)
      return Number.isFinite(num) ? `${num}` : '0'
    },
    formatInspectionCountValue(value) {
      const num = Number(value)
      return Number.isFinite(num) ? `${num}` : '0'
    },
    formatInspectionThirdValue(value) {
      if (this.currentInspectionTypeKey === 'territory') {
        return this.formatInspectionCountValue(value)
      }
      return this.formatInspectionRateValue(value)
    },
    formatInspectionRateValue(value) {
      if (typeof value === 'string' && value.includes('%')) {
        return value
      }
      const num = Number(value)
      return Number.isFinite(num) ? `${num}%` : '0%'
    },
    getTooltip(axisPointerType = 'shadow') {
      return {
        trigger: 'axis',
        backgroundColor: 'rgba(4, 20, 54, 0.92)',
        borderColor: 'rgba(92, 204, 255, 0.34)',
        textStyle: { color: '#e8f8ff', fontSize: 12 },
        axisPointer: {
          type: axisPointerType,
          shadowStyle: { color: 'rgba(98, 176, 255, 0.1)' },
          lineStyle: { color: 'rgba(190, 230, 255, 0.35)' }
        }
      }
    },
    getCategoryAxis(labels) {
      return {
        type: 'category',
        data: labels,
        axisLine: { lineStyle: { color: 'rgba(143, 218, 255, 0.26)' } },
        axisTick: { show: false },
        axisLabel: { color: '#bde8ff', fontSize: 13, margin: 10 }
      }
    },
    getValueAxis(name) {
      return {
        type: 'value',
        name,
        minInterval: 1,
        nameTextStyle: { color: '#c7ebff', fontSize: 13, padding: [0, 0, 8, -10] },
        splitLine: { lineStyle: { color: 'rgba(138, 211, 255, 0.15)' } },
        axisLabel: { color: '#a8d8f6', fontSize: 12 }
      }
    },
    resize() {
      this.chartInstances.forEach(chart => {
        if (chart) {
          chart.resize()
        }
      })
    },
    disposeCharts() {
      if (this.inspectionChart && this.inspectionGlobalOutHandler) {
        const zr = this.inspectionChart.getZr()
        if (zr && typeof zr.off === 'function') {
          zr.off('globalout', this.inspectionGlobalOutHandler)
        }
      }
      this.chartInstances.forEach(chart => {
        if (chart) {
          chart.dispose()
        }
      })
      this.chartInstances = []
      this.primaryChart = null
      this.transportChart = null
      this.inspectionChart = null
      this.passengerChart = null
    }
  }
}
</script>

<style scoped>
.wl-dialog-mask {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(3, 16, 43, 0.55);
  backdrop-filter: none;
}

.wl-dialog {
  position: relative;
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding:
    clamp(104px, 5.6vw, 128px)
    clamp(48px, 2.7vw, 66px)
    clamp(30px, 2.8vw, 40px);
  background-position: center;
  background-repeat: no-repeat;
  background-size: 100% 100%;
}

.wl-dialog__tools {
  position: absolute;
  top: clamp(30px, 3vh, 42px);
  right: clamp(40px, 3.5vw, 64px);
  z-index: 3;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: clamp(6px, 0.5vh, 10px);
}

.wl-period-switch {
  display: inline-grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.12vw;
  width: clamp(150px, 10.2vw, 198px);
  padding: 0.16vh 0.18vw;
  border: 1px solid rgba(71, 186, 255, 0.34);
  border-radius: 999vw;
  background:
    linear-gradient(180deg, rgba(11, 78, 164, 0.7), rgba(5, 38, 98, 0.52)),
    rgba(3, 21, 60, 0.58);
  box-shadow: inset 0 0 0.7vw rgba(33, 177, 255, 0.12);
}

.wl-period-switch button {
  height: clamp(20px, 2vh, 26px);
  border: 0;
  border-radius: 999vw;
  color: rgba(220, 245, 255, 0.82);
  font-size: clamp(12px, 0.58vw, 14px);
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02vw;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
}

.wl-period-switch button.active {
  color: #f6feff;
  background:
    linear-gradient(180deg, rgba(21, 210, 255, 0.44), rgba(14, 108, 235, 0.2)),
    linear-gradient(180deg, rgba(13, 91, 211, 0.98), rgba(8, 62, 158, 0.94));
  box-shadow:
    inset 0 0 0 1px rgba(119, 241, 255, 0.5),
    0 0 0.82vw rgba(32, 180, 255, 0.26);
}

.wl-tool-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.42vw;
}

.wl-tool-btn {
  height: clamp(22px, 2.2vh, 28px);
  padding: 0 0.78vw;
  border: 1px solid rgba(97, 205, 255, 0.42);
  border-radius: 0.28vw;
  color: #d9f6ff;
  font-size: clamp(12px, 0.58vw, 14px);
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  cursor: pointer;
  background: rgba(16, 79, 167, 0.5);
  box-shadow: inset 0 0 0.55vw rgba(99, 210, 255, 0.13);
}

.wl-dialog__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
  column-gap: clamp(78px, 5vw, 108px);
  row-gap: clamp(38px, 4.3vh, 54px);
  width: 100%;
  height: 100%;
}

.wl-chart-card {
  position: relative;
  min-width: 0;
  min-height: 0;
  padding: 2.6vh 0.45vw 0.2vh;
}

.wl-chart-header {
  position: absolute;
  top: 0;
  left: 0.2vw;
  right: 0.2vw;
  z-index: 2;
}

.wl-card-title {
  color: #e5f5ff;
  font-size: 0.84vw;
  font-weight: 800;
  letter-spacing: 0.04vw;
  text-shadow: 0 0 0.8vw rgba(64, 194, 255, 0.42);
}

.wl-chart-card :deep(.tab-header) {
  align-items: center;
  height: clamp(26px, 2.9vh, 34px);
  padding-left: 2.5vw;
  padding-right: 0.2vw;
  margin-bottom: 0;
}

.wl-chart-card :deep(.tab-header--switch .tab-titles--switch) {
  width: fit-content;
  min-width: 0;
  padding: 0.08vw;
  border-radius: 0.48vw;
}

.wl-chart-card :deep(.tab-header--switch .tab-text--switch) {
  min-width: 4.9vw;
  height: clamp(19px, 2vh, 23px);
  padding: 0 0.72vw;
  font-size: 0.68vw;
  font-weight: 700;
  border-radius: 0.32vw;
}

.wl-chart-card :deep(.tab-header .tab-text) {
  font-size: 0.84vw;
  font-weight: 800;
  letter-spacing: 0.02vw;
}

.wl-chart-card :deep(.tab-header .tab-text.active) {
  text-shadow: 0 0 0.8vw rgba(64, 194, 255, 0.42);
}

.wl-unit {
  position: absolute;
  top: 4.4vh;
  left: 0.5vw;
  z-index: 2;
  color: #cceeff;
  font-size: 0.74vw;
  font-weight: 700;
}

.wl-chart-box {
  width: 100%;
  height: 100%;
}

.wl-chart-box--passenger {
  height: calc(100% - 6.7vh);
  margin-top: 6.7vh;
}

.wl-passenger-stats {
  position: absolute;
  top: 3vh;
  left: 0.2vw;
  right: 0.2vw;
  z-index: 2;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.65vw;
}

.wl-passenger-stats div {
  height: 4.5vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border: 1px solid rgba(68, 170, 255, 0.36);
  background: rgba(14, 68, 140, 0.36);
}

.wl-passenger-stats span {
  color: rgba(214, 236, 255, 0.68);
  font-size: 0.66vw;
}

.wl-passenger-stats strong {
  margin-top: 0.2vh;
  color: #58bdff;
  font-size: 1vw;
  line-height: 1;
}

.wl-chart-card--inspection {
  display: flex;
  flex-direction: column;
  padding-top: 0;
  overflow: hidden;
}

.wl-chart-card--inspection :deep(.tab-header) {
  align-items: center;
  height: clamp(26px, 2.9vh, 34px);
  gap: 0.42vw;
  padding-top: 0;
  padding-left: 3.18vw;
  padding-right: 0.26vw;
  padding-bottom: 0;
  margin-bottom: 0.18vh;
}

.wl-chart-card--inspection :deep(.tab-text) {
  height: 100%;
  color: #eefaff;
  font-size: 0.84vw;
  font-weight: 800;
  letter-spacing: 0.02vw;
  text-shadow: 0 0 0.76vw rgba(255, 255, 255, 0.34);
}

.wl-chart-card--inspection .inspection-tab-btn {
  min-width: 4.86vw;
  height: 2.22vh;
  padding: 0 0.58vw;
  font-size: 0.56vw;
}

.inspection-chart-shell {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
}

.inspection-chart-head {
  display: flex;
  align-items: flex-start;
  gap: 0.32vw;
  padding: 0.42vh 0.08vw 0;
}

.inspection-axis-unit {
  display: inline-flex;
  justify-content: center;
  flex: 0 0 auto;
  min-width: 2.2vw;
  padding-top: 1.52vh;
  color: #cbeeff;
  font-size: 0.82vw;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
  text-shadow: 0 0 0.72vw rgba(169, 223, 255, 0.36);
}

.inspection-summary {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  flex: 1;
  min-width: 0;
  gap: 0.32vw;
  padding-top: 0.18vh;
}

.inspection-legend-item {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3vw;
  color: #dff4ff;
  font-size: 0.56vw;
  font-weight: 600;
  white-space: nowrap;
}

.inspection-legend-swatch {
  width: 1.12vw;
  height: 0.13vh;
  border-radius: 999vw;
  box-shadow: 0 0 0.58vw currentColor;
}

.inspection-metric {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.66vh;
  min-width: 0;
}

.inspection-metric-label {
  width: 100%;
}

.inspection-value {
  font-size: 0.98vw;
  font-weight: 700;
  line-height: 1;
  text-align: center;
  text-shadow: 0 0 0.58vw rgba(255, 255, 255, 0.14);
}

.inspection-value--pending {
  color: #18d7ff;
}

.inspection-value--today {
  color: #d5dbe8;
}

.inspection-value--rate {
  color: #91ef47;
}

.inspection-chart-box {
  flex: 1;
  min-height: 0;
  margin-top: 0.34vh;
}

.wl-dialog-mask :deep(.stats-dialog-layer) {
  position: fixed;
  z-index: 90;
}
</style>
